import { useEffect, useState } from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { BrandingTarget } from "types/landlord/address";
import { PMSNames } from "views/settings-integration/integrations/pms/constants";
import {
  formatLandlordFields,
  getTargetName,
  overriddenPmsFields,
  propertiesLabel,
  type LandlordField
} from "./helpers";

export const ConfirmApplyBrandingDialog = ({
  opened,
  onClose,
  onConfirm,
  sourceName,
  selectedCount,
  overwriteTargets,
  pmsTargets,
  appliedLandlordFields,
  isAllMode,
  isPending,
  canClose
}: {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sourceName: string;
  selectedCount: number;
  overwriteTargets: BrandingTarget[];
  pmsTargets: BrandingTarget[];
  appliedLandlordFields: LandlordField[];
  isAllMode: boolean;
  isPending: boolean;
  canClose: boolean;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const [acknowledged, setAcknowledged] = useState(false);

  const needsAcknowledgement =
    appliedLandlordFields.length > 0 && (isAllMode || pmsTargets.length > 0);

  useEffect(() => {
    if (opened) setAcknowledged(false);
  }, [opened]);

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      panelClassName="max-w-[720px] w-full"
    >
      <header className="flex items-start justify-between gap-4 border-b border-alpha-40 px-6 py-6">
        <h3 className="text-xl font-bold text-content-1000">
          Apply branding to {selectedCount}{" "}
          {propertiesLabel(selectedCount, dictionary)}?
        </h3>
        <IconButton
          HugeIcon={Cancel01Icon}
          disabled={!canClose}
          onClick={onClose}
          size={IconButtonSize.BASE}
          variant={IconButtonVariant.TEXT_BASE}
        />
      </header>

      {overwriteTargets.length > 0 || needsAcknowledgement ? (
        <div className="space-y-3 border-b border-alpha-40 px-6 py-4">
          {overwriteTargets.length > 0 ? (
            <p className="rounded-lg bg-warning-50 p-4 text-sm text-warning-800">
              <span className="font-bold">
                {overwriteTargets.length} of the {selectedCount}{" "}
                {propertiesLabel(selectedCount, dictionary)}
              </span>{" "}
              you selected already have their own branding set. Applying will
              overwrite their existing branding with the values from{" "}
              {sourceName}.
            </p>
          ) : null}

          {needsAcknowledgement ? (
            <p className="rounded-lg bg-warning-50 p-4 text-sm text-warning-800">
              <span className="font-bold">
                Landlord contact fields will stop syncing from the PMS
              </span>{" "}
              {isAllMode
                ? `on any ${dictionary.property} that currently syncs ${formatLandlordFields(appliedLandlordFields).toLowerCase()}.`
                : `on ${pmsTargets.length} ${propertiesLabel(pmsTargets.length, dictionary)}.`}{" "}
              Boom will keep the values applied here, and future PMS syncs will
              leave those fields alone. Logo, primary color, and palette are not
              affected — no PMS syncs them.
            </p>
          ) : null}
        </div>
      ) : null}

      {pmsTargets.length > 0 ? (
        <div className="max-h-[240px] overflow-y-auto border-b border-alpha-40 px-6 py-4">
          {pmsTargets.map((target) => (
            <div
              className="flex items-center justify-between gap-4 py-2"
              key={target.id}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-content-700">
                  {getTargetName(target, dictionary)}
                </p>
                <p className="truncate text-sm text-content-300">
                  {target.address || "No address"}
                </p>
              </div>
              <p className="shrink-0 text-xs font-bold text-warning-700">
                {target.pms_name ? `${PMSNames[target.pms_name]} · ` : ""}
                {formatLandlordFields(
                  overriddenPmsFields(target, appliedLandlordFields)
                )}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      {needsAcknowledgement ? (
        <div className="border-b border-alpha-40 px-6 py-4">
          <Checkbox
            checked={acknowledged}
            id="branding-pms-ack"
            label="I understand this overwrites the landlord contact fields"
            onChange={(e) => setAcknowledged(e.target.checked)}
            size={CheckboxSize.SM}
            subLabelText="Boom keeps the values applied here and stops syncing them from the PMS going forward."
          />
        </div>
      ) : null}

      <footer className="flex items-center justify-end gap-2 px-6 py-4">
        <Button disabled={!canClose} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={needsAcknowledgement && !acknowledged}
          loading={isPending}
          onClick={onConfirm}
          variant={ButtonVariant.PRIMARY}
        >
          Apply branding
        </Button>
      </footer>
    </Modal>
  );
};
